/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Test run: pick a record and see what the automation on the canvas (saved or
 * not) would do to it, step by step. POST /automations/test changes nothing.
 * The path it reports is highlighted on the canvas by the caller.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Check, CircleSlash, Search } from "lucide-react";
import { useAuth } from "auth/Auth";
import { v2 as t } from "styles/tokens/v2";

const label = (r) =>
  r.name || [r.first_name, r.last_name].filter(Boolean).join(" ") || r.subject || r.title || r.case_number || r.order_number || r.invoice_number || `#${r.id}`;

export default function TestRunDialog({ open, onClose, object, objectName, buildBody, onResult }) {
  const { axiosInstance } = useAuth();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setResult(null);
      setError("");
    }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2 || /^\d+$/.test(q)) {
      setHits([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      axiosInstance
        .get("/search", { params: { query: q } })
        .then(({ data }) => {
          if (cancelled) return;
          setHits((data || []).filter((r) => r.type_singular === object || r.type === object).slice(0, 8));
        })
        .catch(() => !cancelled && setHits([]))
        .finally(() => !cancelled && setSearching(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, object, axiosInstance]);

  const run = async (recordId) => {
    setRunning(true);
    setError("");
    try {
      const { data } = await axiosInstance.post("/automations/test", { ...buildBody(), record_id: Number(recordId) });
      setResult(data);
      onResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || (e?.response?.status === 404 ? "That record was not found, or you can't see it." : "The test run failed."));
    } finally {
      setRunning(false);
    }
  };

  const idTyped = /^\d+$/.test(query.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Test run</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: t.text2, mb: 2 }}>
          Pick a {objectName || "record"} to see what this automation would do to it. Nothing is changed and no email is sent.
        </Typography>
        {!result && (
          <>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={`Search ${objectName || "records"} by name, or type a record number`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputProps={{ "aria-label": "Find a record" }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} color={t.text3} /></InputAdornment> }}
            />
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
              {idTyped && (
                <ButtonBase onClick={() => run(query.trim())} disabled={running} sx={{ justifyContent: "flex-start", p: 1.25, borderRadius: "8px", border: `1px solid ${t.border}`, fontFamily: "inherit", fontSize: 14 }}>
                  Use {objectName || "record"} #{query.trim()}
                </ButtonBase>
              )}
              {hits.map((r) => (
                <ButtonBase key={r.id} onClick={() => run(r.id)} disabled={running} sx={{ justifyContent: "flex-start", flexDirection: "column", alignItems: "flex-start", p: 1.25, borderRadius: "8px", border: `1px solid ${t.border}`, fontFamily: "inherit", "&:hover": { bgcolor: t.hover } }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>{label(r)}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: t.text2 }}>{[r.email, r.company_name, `#${r.id}`].filter(Boolean).join(" · ")}</Typography>
                </ButtonBase>
              ))}
              {searching && <CircularProgress size={18} sx={{ alignSelf: "center", mt: 1 }} />}
              {!searching && query.trim().length >= 2 && !idTyped && !hits.length && (
                <Typography sx={{ fontSize: 13.5, color: t.text2 }}>No {objectName || "records"} match. Try another name, or type the record number.</Typography>
              )}
            </Box>
          </>
        )}
        {running && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 14 }}>Working it out…</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {result && (
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text, mb: 1 }}>{result.record_label}</Typography>
            {!result.matched_trigger_conditions ? (
              <Alert severity="info">{result.message}</Alert>
            ) : (
              <Box component="ol" sx={{ m: 0, pl: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.75 }}>
                {result.path.map((p, i) => (
                  <Box component="li" key={`${p.step_id}-${i}`} sx={{ display: "flex", gap: 1, alignItems: "flex-start", fontSize: 14, color: t.text }}>
                    {p.kind === "condition" && p.outcome === "no" ? <CircleSlash size={16} color="#8A4B08" /> : <Check size={16} color="#166534" />}
                    <span>{p.preview}</span>
                  </Box>
                ))}
                {!result.path.length && <Typography sx={{ fontSize: 14, color: t.text2 }}>No steps would run.</Typography>}
              </Box>
            )}
            <Typography sx={{ fontSize: 13, color: t.text2, mt: 1.5 }}>The steps it would take are outlined in green on the canvas.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result && <Button onClick={() => setResult(null)}>Try another record</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

TestRunDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  object: PropTypes.string,
  objectName: PropTypes.string,
  buildBody: PropTypes.func.isRequired,
  onResult: PropTypes.func.isRequired,
};
TestRunDialog.defaultProps = { object: "", objectName: "" };
